import { makeAutoObservable } from 'mobx'
import {
  ChatbotActionType,
  ChatbotDisqualifyBehaviorStatus,
  ChatbotField,
  type IChatbotActionTag,
  type IChatbotFullData,
} from 'entities/Chatbot'
import { CustomFieldsStore } from 'entities/CustomField'
import { ContactsApi } from 'entities/Contacts'
import { TagsApi } from 'entities/Tags'
import { Tag } from 'entities/Tags/model/Tag'
import { fetchSendFromInfo, type ISendFromInfo } from 'entities/SendFrom'
import type { IEntitiesStore } from 'entities/EntitiesExport'

const FIELDS_LIMIT = 100

export class DetailsStore {
  private _fields!: ChatbotField[]
  private _fieldsStore = new CustomFieldsStore(FIELDS_LIMIT)

  private _futureSendFrom!: Promise<ISendFromInfo | null>
  private _futureContactsCount!: Promise<number | null>
  private _futureActionsTags = new Map<string, Promise<Tag[]>>()

  constructor(private _data: IChatbotFullData, private _entities: IEntitiesStore) {
    this._initFields()
    this._initSendFrom()
    this._initContactsCount()
    this._initActionTags()

    makeAutoObservable(this)
  }

  get data() {
    return this._data
  }

  get fields() {
    return this._fields
  }

  get dataCollectionInstruction() {
    return this._data.settings?.data_collection_instruction?.value || ''
  }

  get qualifyInstruction() {
    return this._data.settings?.qualify_instruction?.value || ''
  }

  get qualifyEnabled() {
    return this._data.settings?.qualify_enabled?.value ?? false
  }

  get qualifiedActions() {
    return this._data.qualified_actions
  }

  get disqualifiedActions() {
    return this._data.disqualified_actions
  }

  get disqualifiedBehavior() {
    if (this._data.settings?.disqualify_behavior?.value === ChatbotDisqualifyBehaviorStatus.Stop) {
      return 'Politely conclude conversation'
    }

    if (
      this._data.settings?.disqualify_behavior?.value === ChatbotDisqualifyBehaviorStatus.Continue
    ) {
      return 'Continue to success or fallback actions'
    }

    return undefined
  }

  get futureSendFrom() {
    return this._futureSendFrom
  }

  get futureFutureContactsCount() {
    return this._futureContactsCount
  }

  getFutureActionsTags = (ids: number[]) => {
    const key = ids.join(',')
    return this._futureActionsTags.get(key) ?? Promise.resolve([])
  }

  getFieldName = (fieldKey: string) => {
    return this._fieldsStore.fields.find(({ key }) => key === fieldKey)?.name
  }

  loadData = () => Promise.all([this._fieldsStore.loadData()])

  private _initFields = () => {
    this._fields = this._data.fields?.map(ChatbotField.Create) ?? []
    if (!this._fields.length) this._fields = [ChatbotField.Create()]
  }

  private _initSendFrom = () => {
    this._futureSendFrom = fetchSendFromInfo(this._data.send_from, this._entities)
  }

  private _initContactsCount = () => {
    if (!this._data.filters) return (this._futureContactsCount = Promise.resolve(null))

    const filtersSize = Object.values(this._data.filters)
      .map((filter) => filter.length ?? 0)
      .reduce((prev, next) => prev + next, 0)
    if (!filtersSize) return (this._futureContactsCount = Promise.resolve(null))

    this._futureContactsCount = ContactsApi.getContactsFiltersCount({
      filtersListGroups: this._data.filters,
    }).then(
      ({ data: { count } }) => count,
      () => null
    )
  }

  private _initActionTags = () => {
    const allActions = [
      ...(this._data.success_actions ?? []),
      ...(this._data.fallback_actions ?? []),
      ...(this._data.qualified_actions ?? []),
      ...(this._data.disqualified_actions ?? []),
    ]

    allActions
      .filter(
        (action) =>
          action.type === ChatbotActionType.AddTag || action.type === ChatbotActionType.RemoveTag
      )
      .map((action) => action.settings as IChatbotActionTag)
      .forEach((settings) => {
        const ids = settings.tag_ids
        const key = ids.join(',')
        const futureTags = TagsApi.getTagsById(ids).then(
          (tags) => tags.map((tag) => new Tag(tag)),
          () => []
        )

        this._futureActionsTags.set(key, futureTags)
      })
  }
}
